/**
 * AppSyncStatus Component
 *
 * Wrapper component for SyncStatusHeader that consumes cached settings from context.
 * Use this in main app pages via AppLayout's headerContent prop, or in the onboarding
 * wizard with explicit providerKey override.
 *
 * Features:
 * - Consumes cached settings from UserSyncSettingsContext (loaded once in PrivateAppProvider)
 * - Subscribes to BackgroundSyncService for lastSyncAt and syncStatus
 * - Provides "Sync Now" functionality
 * - Returns null while loading or if no provider configured (unless providerKey prop passed)
 * - Automatically updates when settings change (via realtime subscription)
 * - Memoized for performance
 * - Supports both 'main' and 'wizard' variants
 *
 * @example Main app (default behavior)
 * ```tsx
 * <AppLayout headerContent={<AppSyncStatus />}>
 *   {children}
 * </AppLayout>
 * ```
 *
 * @example Wizard with explicit provider
 * ```tsx
 * <AppSyncStatus variant="wizard" providerKey="google-drive" />
 * ```
 */

import { memo, useState, useEffect, useCallback, useRef } from 'react';

import { useUserSyncSettings } from '@/contexts';
import { BackgroundSyncService, type SyncStatus } from '@/services/sync/BackgroundSyncService';
import { DeviceService } from '@/services/DeviceService';
import type { ProviderKey, SchemaVersionError } from '@/types/sync';

import { SyncStatusHeader } from '../SyncStatusHeader';
import type { SyncStatusHeaderVariant } from '../SyncStatusHeader/SyncStatusHeader.types';

/**
 * Valid provider keys that SyncStatusHeader accepts
 */
const VALID_PROVIDER_KEYS: readonly ProviderKey[] = ['google-drive', 'local'] as const;

/**
 * Type guard to check if a value is a valid ProviderKey
 */
function isValidProviderKey(value: unknown): value is ProviderKey {
  return typeof value === 'string' && VALID_PROVIDER_KEYS.includes(value as ProviderKey);
}

/**
 * Props for AppSyncStatus component
 */
export interface AppSyncStatusProps {
  /**
   * Context variant determining UI behavior.
   * - 'main': Main app context (default), shows settings link
   * - 'wizard': Onboarding wizard context, no settings link
   * @default 'main'
   */
  variant?: SyncStatusHeaderVariant;

  /**
   * Override provider from context. When provided, skips context-based null checks.
   * Use this in wizard context where provider is already validated by caller.
   */
  providerKey?: ProviderKey;

  /**
   * Override showSettingsLink behavior.
   * @default true for 'main' variant, false for 'wizard' variant
   */
  showSettingsLink?: boolean;
}

/**
 * AppSyncStatus - Self-contained sync status for app headers
 *
 * Consumes cached settings from context and renders SyncStatusHeader
 * when a valid provider is configured. Subscribes to BackgroundSyncService
 * for sync status and last sync time.
 *
 * When providerKey prop is passed, skips context loading/validation checks
 * (caller is responsible for ensuring valid provider).
 */
function AppSyncStatusComponent(props: AppSyncStatusProps): React.ReactElement | null {
  const {
    variant = 'main',
    providerKey: providerKeyProp,
    showSettingsLink: showSettingsLinkProp,
  } = props;

  const { settings, isLoading } = useUserSyncSettings();

  // Determine effective provider key: prop takes precedence over context
  const effectiveProviderKey = providerKeyProp ?? settings?.activeSyncProvider;

  // Determine effective showSettingsLink: prop takes precedence, else derive from variant
  const effectiveShowSettingsLink = showSettingsLinkProp ?? (variant === 'main');

  // Sync state from BackgroundSyncService
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [schemaVersionError, setSchemaVersionError] = useState<SchemaVersionError | null>(null);

  // Track cancelled state and unsubscribe functions for cleanup
  const cancelledRef = useRef(false);
  const unsubscribeStatusRef = useRef<(() => void) | null>(null);
  const unsubscribeCompletedRef = useRef<(() => void) | null>(null);
  const unsubscribeSchemaVersionErrorRef = useRef<(() => void) | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to BackgroundSyncService on mount (with retry pattern)
  useEffect(() => {
    cancelledRef.current = false;
    let retryCount = 0;
    const MAX_RETRIES = 20;
    const RETRY_INTERVAL_MS = 300;

    const syncService = BackgroundSyncService.getInstance();

    // Subscribe to schema version errors IMMEDIATELY (defensive hardening)
    // This subscription doesn't need the service to be "running" - errors can be
    // set at any time (e.g., from dev tools or during initialization).
    // Initialize from current state first
    const initialSchemaError = syncService.getSchemaVersionError();
    if (initialSchemaError) {
      console.log('[AppSyncStatus] Initial schema version error:', initialSchemaError.code);
      setSchemaVersionError(initialSchemaError);
    }

    // Subscribe to schema version error changes
    unsubscribeSchemaVersionErrorRef.current = syncService.onSchemaVersionError((error) => {
      console.log('[AppSyncStatus] onSchemaVersionError:', error?.code ?? 'cleared');
      if (!cancelledRef.current) {
        setSchemaVersionError(error);
      }
    });

    /**
     * Attempt to subscribe to BackgroundSyncService.
     * Retries until service is running or max retries exceeded.
     */
    const attemptSubscription = (): boolean => {
      // Check if service is running (not just has provider)
      // This ensures manifest has been loaded before we read lastSyncTime
      if (!syncService.getIsRunning()) {
        return false; // Not ready yet
      }

      // Initialize status from service (use manifest value as fallback)
      const initialStatus = syncService.getStatus();
      const manifestLastSync = syncService.getLastSyncTime();
      console.log('[AppSyncStatus] ✅ Subscribed to service:', { initialStatus, manifestLastSync, retryCount });
      setSyncStatus(initialStatus);
      setLastSyncAt(manifestLastSync); // Fallback until DB fetch completes

      // Fetch lastSyncAt from database (source of truth)
      // This runs async after subscription to get accurate time
      const syncEngine = syncService.getSyncEngine();
      if (syncEngine) {
        const userId = syncEngine.getUserId();
        const deviceId = syncEngine.getDeviceId();
        const deviceService = new DeviceService();

        deviceService.getDeviceByDeviceId(userId, deviceId)
          .then((device) => {
            if (!cancelledRef.current && device?.lastSyncAt) {
              console.log('[AppSyncStatus] 📊 DB lastSyncAt:', device.lastSyncAt);
              setLastSyncAt(device.lastSyncAt);
            }
          })
          .catch((error) => {
            console.warn('[AppSyncStatus] Failed to fetch device from DB:', error);
            // Keep manifest value as fallback
          });
      }

      // Subscribe to status changes (store unsubscribe function)
      unsubscribeStatusRef.current = syncService.onStatusChanged((status) => {
        console.log('[AppSyncStatus] onStatusChanged:', status);
        if (!cancelledRef.current) {
          setSyncStatus(status);
        }
      });

      // Subscribe to sync completion (store unsubscribe function)
      unsubscribeCompletedRef.current = syncService.onSyncCompleted((result) => {
        console.log('[AppSyncStatus] onSyncCompleted:', { success: result.success, dataExportedAt: result.dataExportedAt });
        if (!cancelledRef.current && result.success) {
          // Use service's getLastSyncTime() which is updated on every successful sync
          // (more reliable than result.dataExportedAt which comes from the delta timestamp)
          const lastSyncTime = syncService.getLastSyncTime();
          console.log('[AppSyncStatus] Setting lastSyncAt to:', lastSyncTime);
          setLastSyncAt(lastSyncTime);
        }
      });

      // Note: Schema version error subscription moved outside retry loop
      // (see top of useEffect) - it doesn't require service to be running

      return true; // Successfully subscribed
    };

    /**
     * Retry loop - polls until provider ready or max retries
     */
    const startRetryLoop = () => {
      // Clear any existing retry timer
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      // Try to subscribe immediately
      if (attemptSubscription()) {
        return; // Success on first try
      }

      // Start retry loop
      const retry = () => {
        if (cancelledRef.current) {
          return; // Cleanup was called
        }

        retryCount++;
        if (retryCount > MAX_RETRIES) {
          console.warn('[AppSyncStatus] ⚠️ Max retries exceeded, giving up subscription');
          return;
        }

        console.log(`[AppSyncStatus] Provider not ready, retry ${retryCount}/${MAX_RETRIES}...`);

        if (attemptSubscription()) {
          return; // Success
        }

        // Schedule next retry
        retryTimerRef.current = setTimeout(retry, RETRY_INTERVAL_MS);
      };

      // Start first retry after delay
      retryTimerRef.current = setTimeout(retry, RETRY_INTERVAL_MS);
    };

    // Start subscription attempt
    startRetryLoop();

    // Cleanup function
    return () => {
      cancelledRef.current = true;

      // Clear retry timer
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      // Unsubscribe from listeners
      if (unsubscribeStatusRef.current) {
        unsubscribeStatusRef.current();
        unsubscribeStatusRef.current = null;
      }
      if (unsubscribeCompletedRef.current) {
        unsubscribeCompletedRef.current();
        unsubscribeCompletedRef.current = null;
      }
      if (unsubscribeSchemaVersionErrorRef.current) {
        unsubscribeSchemaVersionErrorRef.current();
        unsubscribeSchemaVersionErrorRef.current = null;
      }
    };
  }, [effectiveProviderKey]); // Re-subscribe when provider changes

  /**
   * Handle Sync Now - wraps BackgroundSyncService.syncNow()
   */
  const handleSyncNow = useCallback(async (): Promise<void> => {
    const syncService = BackgroundSyncService.getInstance();
    const result = await syncService.syncNow();

    if (!result.success && result.error) {
      // Log error - could add toast here if needed
      console.warn('[AppSyncStatus] Sync failed:', result.error);
    }
  }, []);

  // When providerKey prop is passed, skip context-based checks (caller validates)
  // When not passed, apply original guards for main app context
  if (!providerKeyProp) {
    // Don't render while loading (main app context only)
    if (isLoading) {
      return null;
    }

    // Don't render if no settings or no valid provider configured (main app context only)
    if (!settings?.activeSyncProvider || !isValidProviderKey(settings.activeSyncProvider)) {
      return null;
    }
  }

  // At this point, effectiveProviderKey is guaranteed to be valid:
  // - If providerKeyProp was passed, caller validated it
  // - If not, we passed the isValidProviderKey check above
  if (!effectiveProviderKey || !isValidProviderKey(effectiveProviderKey)) {
    return null;
  }

  return (
    <SyncStatusHeader
      variant={variant}
      providerKey={effectiveProviderKey}
      showSettingsLink={effectiveShowSettingsLink}
      lastSyncAt={lastSyncAt}
      syncStatus={syncStatus}
      schemaVersionError={schemaVersionError}
      onSyncNow={handleSyncNow}
    />
  );
}

/**
 * Memoized AppSyncStatus component
 */
export const AppSyncStatus = memo(AppSyncStatusComponent);
AppSyncStatus.displayName = 'AppSyncStatus';
